import { KeyboardEvent as ReactKeyboardEvent, FocusEvent } from "react";
import { ControllerRenderProps, FieldValues, useFormContext } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import {
  AppButtonFormData,
  CustomSelectOption,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

import { BUTTON_WIDTH_OPTIONS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/button/button.constants";

export const useButtonWidth = () => {
  // context
  const { setButton } = useThemePreviewContext();

  //form
  const { setValue } = useFormContext();

  // Width-specific functions
  const getButtonWidthSelectValue = (field: CustomSelectOption) => {
    const matchingOption = BUTTON_WIDTH_OPTIONS.find((option) => option.value === field.value);

    const label = (
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center">{matchingOption?.icon}</div>
        <span className="truncate">{field.label}</span>
      </div>
    );

    return {
      value: field.value,
      label: label,
    };
  };

  const handleWidthChange = (
    value: CustomSelectOption,
    fieldOnChange: (value: CustomSelectOption) => void,
  ) => {
    fieldOnChange(value);

    // Reset custom value to 50 when switching away from "fixed"
    if (value.value !== "fixed") {
      setValue("button.width.custom", "50", { shouldDirty: true });
    }

    setButton((prev: AppButtonFormData | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        width: {
          name: value,
          custom: value.value === "fixed" ? prev.width.custom : "50",
        },
      };
    });
  };

  const handleWidthCustomChange = (value: string, fieldOnChange: (value: string) => void) => {
    // Clean up the value like simple padding
    let cleanedValue = value;

    // remove leading zeros
    cleanedValue = cleanedValue.replace(/^0+(?=\d)/, "");

    // limit to 3 digits
    if (cleanedValue.length > 3) {
      cleanedValue = cleanedValue.slice(0, 3);
    }

    fieldOnChange(cleanedValue);

    // Only update context if value is not empty and within valid range
    if (cleanedValue !== "" && Number(cleanedValue) >= 20 && Number(cleanedValue) <= 100) {
      setButton((prev: AppButtonFormData | null) => {
        if (!prev) return prev;
        return {
          ...prev,
          width: { name: prev.width.name, custom: cleanedValue },
        };
      });
    }
  };

  // helper to update width with arrows
  const widthArrowHelper = (field: ControllerRenderProps<FieldValues, string>, num: number) => {
    const newValue = Math.max(20, Math.min(100, Number(field.value) + num));
    handleWidthCustomChange(String(newValue), field.onChange);
  };

  const handleWidthBlur = (
    event: FocusEvent<HTMLInputElement>,
    field: ControllerRenderProps<FieldValues, string>,
  ) => {
    const originalValue = event.target.value;
    let numValue = Number(originalValue);

    // If invalid or less than 20, set to 20
    if (Number.isNaN(numValue) || numValue < 20) {
      numValue = 20;
    }
    // If greater than 100, set to 100
    else if (numValue > 100) {
      numValue = 100;
    }
    // Round to nearest multiple of 5
    else {
      numValue = Math.round(numValue / 5) * 5;
      // Ensure it stays within bounds
      numValue = Math.max(20, Math.min(100, numValue));
    }

    const value = String(numValue);

    // Only call handleWidthCustomChange if the value was modified
    if (value !== originalValue) {
      handleWidthCustomChange(value, field.onChange);
    }
  };

  // handle key down to prevent invalid characters for width
  const handleWidthKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>, value: string) => {
    // do not allow these in number field
    if (["e", "E", ".", "-", "+"].includes(event.key)) {
      event.preventDefault();
    }

    // only allow ArrowDown if value > 20
    if (event.key === "ArrowDown" && Number(value) <= 20) {
      event.preventDefault();
    }
  };

  return {
    getButtonWidthSelectValue,
    handleWidthChange,
    handleWidthCustomChange,
    widthArrowHelper,
    handleWidthBlur,
    handleWidthKeyDown,
  };
};
